import React, { useState } from "react";
import {
    Box,
    Stack,
    Typography,
    Select,
    selectClasses,
    outlinedInputClasses,
    MenuItem,
    ButtonBase,
    useTheme,
    SelectChangeEvent,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PersonRounded from "@mui/icons-material/PersonRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import GalleryImage from "../../GalleryImage";
import ColoredIcon from "../../ColoredIcon";
import ShareIcon from "../../../img/Link.svg";
import ChatIcon from "../../../img/Chat.svg";
import { useNavigate } from "react-router-dom";
import User from "../../../api/User";
import { useCookies } from "react-cookie";
import { TAG_COLORS } from "../../../utils/tagColors";
import { FONTS, CARD_STYLES, FORM_LABEL_SX } from "../../../constants/styles";

export interface ProfileAction {
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    variant: "primary" | "secondary";
}

interface UserProfileHeaderProps {
    user: User;
    expandablePhoto?: boolean;
    actions?: ProfileAction[];
}

const UserProfileHeader: React.FC<UserProfileHeaderProps> = ({
    user,
    expandablePhoto = false,
    actions,
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [selectedTag, setSelectedTag] = useState(user.status || "New");
    const [{ uuid, brand_id }] = useCookies(["uuid", "brand_id"]);

    const onUserStatusChange = async (event: SelectChangeEvent<string>) => {
        const status = event.target.value;
        setSelectedTag(status);
        await User.updateStatus(uuid, brand_id, user.key, status);
    };

    const onPhotoOrNameClick = () => navigate(`/users/info/${user.key}`);

    // statusColor will be used for tag styling
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const statusColor =
        TAG_COLORS.find((t) => t.tag === selectedTag)?.color ?? primary;

    const iconBoxSx = {
        width: 36,
        height: 36,
        borderRadius: "12px",
        bgcolor: alpha(primary, 0.08),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    return (
        <Stack
            sx={{
                ...CARD_STYLES,
                p: 0,
                overflow: "hidden",
            }}
        >
            {/* Identity hero area */}
            <Stack
                sx={{
                    background: `linear-gradient(135deg, ${alpha(primary, 0.04)} 0%, ${alpha(primary, 0.08)} 100%)`,
                    px: 2,
                    py: 2.5,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    {/* Avatar */}
                    {user.image ? (
                        <GalleryImage
                            image={user.image}
                            width={56}
                            height={56}
                            borderRadius="16px"
                            expandable={expandablePhoto}
                        />
                    ) : (
                        <ButtonBase
                            onClick={onPhotoOrNameClick}
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: "16px",
                                bgcolor: alpha(primary, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flex: "0 0 auto",
                            }}
                        >
                            <PersonRounded
                                sx={{
                                    fontSize: 26,
                                    color: primary,
                                    opacity: 0.6,
                                }}
                            />
                        </ButtonBase>
                    )}

                    {/* Name + Total */}
                    <Stack flex={1} spacing={0.5} sx={{ minWidth: 0 }}>
                        <Typography
                            className="no-translation"
                            fontFamily={FONTS.display}
                            fontWeight={600}
                            fontSize="18px"
                            lineHeight={1.3}
                            noWrap
                            onClick={onPhotoOrNameClick}
                            sx={{ cursor: "pointer" }}
                        >
                            {user.name}
                        </Typography>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                        >
                            <Typography
                                fontSize="13px"
                                color="text.secondary"
                            >
                                Total:
                            </Typography>
                            <Typography
                                fontFamily={FONTS.display}
                                fontWeight={700}
                                fontSize="15px"
                                color={primary}
                            >
                                {user.total ?? 0}
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* Action button */}
                    {user.rkey ? (
                        <ButtonBase
                            sx={iconBoxSx}
                            onClick={() =>
                                navigate(`/users/link/${user.key}`)
                            }
                        >
                            <ColoredIcon
                                icon={ShareIcon}
                                size="18px"
                                color={primary}
                            />
                        </ButtonBase>
                    ) : (
                        <ButtonBase
                            onClick={() => navigate(`/chats/${user.key}`)}
                            sx={iconBoxSx}
                        >
                            <ColoredIcon
                                icon={ChatIcon}
                                size="18px"
                                color={primary}
                            />
                        </ButtonBase>
                    )}
                </Stack>
            </Stack>

            {/* Status selector strip */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 2, py: 1.25 }}
            >
                <Typography sx={FORM_LABEL_SX}>Status</Typography>
                <Select
                    value={selectedTag}
                    MenuProps={{ disableScrollLock: true }}
                    size="small"
                    sx={{
                        minWidth: 100,
                        [`& .${selectClasses.select}`]: {
                            py: 0.5,
                            px: 1,
                            display: "flex",
                            alignItems: "center",
                        },
                        [`& .${outlinedInputClasses.notchedOutline}`]: {
                            borderRadius: "10px",
                            borderColor: "rgba(0,0,0,0.08)",
                        },
                        "&:hover": {
                            [`& .${outlinedInputClasses.notchedOutline}`]: {
                                borderColor: "rgba(0,0,0,0.15)",
                            },
                        },
                        "&.Mui-focused": {
                            [`& .${outlinedInputClasses.notchedOutline}`]: {
                                borderColor: alpha(primary, 0.3),
                                borderWidth: "1px",
                            },
                        },
                    }}
                    onChange={onUserStatusChange}
                    renderValue={(value) => {
                        const tagColor =
                            TAG_COLORS.find((t) => t.tag === value)?.color ??
                            primary;
                        return (
                            <Stack
                                sx={{
                                    bgcolor: alpha(tagColor, 0.1),
                                    borderRadius: "8px",
                                    px: 1,
                                    py: 0.25,
                                }}
                            >
                                <Typography
                                    className="force-dynamic-translation"
                                    fontWeight={500}
                                    fontSize="12px"
                                    color={tagColor}
                                >
                                    {value}
                                </Typography>
                            </Stack>
                        );
                    }}
                >
                    {TAG_COLORS.map((tag) => (
                        <MenuItem key={tag.tag} value={tag.tag}>
                            <Stack
                                sx={{
                                    bgcolor: alpha(tag.color, 0.1),
                                    borderRadius: "8px",
                                    px: 1,
                                    py: 0.25,
                                }}
                            >
                                <Typography
                                    className="force-dynamic-translation"
                                    fontWeight={500}
                                    fontSize="12px"
                                    color={tag.color}
                                >
                                    {tag.tag}
                                </Typography>
                            </Stack>
                        </MenuItem>
                    ))}
                </Select>
            </Stack>

            {/* Action tiles — opt-in via `actions` prop */}
            {actions && actions.length > 0 && (
                <>
                    <Box
                        sx={{
                            height: "1px",
                            mx: 2,
                            bgcolor: alpha(primary, 0.08),
                        }}
                    />
                    <Stack spacing={0.75} sx={{ px: 1.5, py: 1.25 }}>
                        {actions.map((action) => (
                            <ActionTile
                                key={action.key}
                                action={action}
                                primary={primary}
                            />
                        ))}
                    </Stack>
                </>
            )}
        </Stack>
    );
};

const ActionTile: React.FC<{
    action: ProfileAction;
    primary: string;
}> = ({ action, primary }) => {
    const isPrimary = action.variant === "primary";

    return (
        <ButtonBase
            onClick={action.onClick}
            sx={{
                width: "100%",
                borderRadius: "12px",
                px: 1.25,
                py: 1,
                bgcolor: alpha(primary, isPrimary ? 0.1 : 0.04),
                border: `1px solid ${alpha(primary, isPrimary ? 0.18 : 0.08)}`,
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                textAlign: "left",
                transition:
                    "background-color 0.18s ease, transform 0.12s ease",
                "&:hover": {
                    bgcolor: alpha(primary, isPrimary ? 0.14 : 0.07),
                },
                "&:active": {
                    transform: "scale(0.985)",
                },
            }}
        >
            <Box
                sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    bgcolor: alpha(primary, isPrimary ? 0.18 : 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    color: primary,
                }}
            >
                {action.icon}
            </Box>
            <Typography
                sx={{
                    flex: 1,
                    fontFamily: FONTS.display,
                    fontWeight: isPrimary ? 600 : 500,
                    fontSize: "14px",
                    color: isPrimary ? "text.primary" : "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                }}
            >
                {action.label}
            </Typography>
            <ChevronRightRounded
                sx={{
                    fontSize: 18,
                    color: alpha(primary, 0.5),
                    flex: "0 0 auto",
                }}
            />
        </ButtonBase>
    );
};

export default UserProfileHeader;
